"use client";

import {
  Box, Button, Divider, Paper, TextField, Typography, CircularProgress,
  InputAdornment, Avatar, Grid, Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { getSweetAlert, maskPhone } from "../../helper";

export default function AppSettings() {
  const token = useSelector((state) => state.loginSlice.token);

  // ── App Settings ──
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [promoPercent, setPromoPercent] = useState("");

  useEffect(() => {
    axiosRequest("GET", `${apiBaseUrl}/app-setting`, token)
      .then((res) => setPromoPercent(res?.data?.promo_conversion_percent ?? ""))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const val = Number(promoPercent);
    if (isNaN(val) || val < 0 || val > 100) {
      getSweetAlert("error", "Enter a value between 0 and 100");
      return;
    }
    setSaving(true);
    const res = await axiosRequest("PUT", `${apiBaseUrl}/app-setting`, token, {
      promo_conversion_percent: val,
    });
    setSaving(false);
    if (res?.data || res?.status) {
      getSweetAlert("success", "Settings saved successfully");
    } else {
      getSweetAlert("error", res?.message || "Failed to save settings");
    }
  };

  // ── On Table Chips ──
  const [otChips, setOtChips]       = useState([]);
  const [otQty, setOtQty]           = useState({});
  const [otLoading, setOtLoading]   = useState(false);
  const [otSaving, setOtSaving]     = useState(false);

  useEffect(() => {
    Promise.all([
      axiosRequest("GET", `${apiBaseUrl}/chips`, token),
      axiosRequest("GET", `${apiBaseUrl}/shift-management/current-summary`, token),
    ]).then(([chipsRes, summaryRes]) => {
      const chips = Array.isArray(chipsRes?.data) ? chipsRes.data : (chipsRes?.data?.data ?? []);
      setOtChips(chips);
      const onTable = summaryRes?.data?.on_table_chips || {};
      const init = {};
      chips.forEach((c) => { init[c.denomination] = String(onTable[String(c.denomination)] ?? onTable[c.denomination] ?? ""); });
      setOtQty(init);
    });
  }, []);

  const handleOtSave = async () => {
    const chip_denominations = {};
    Object.entries(otQty).forEach(([d, q]) => { if (Number(q) >= 0) chip_denominations[d] = Number(q); });
    setOtSaving(true);
    const res = await axiosRequest("PATCH", `${apiBaseUrl}/shift-management/on-table-chips`, token, { chip_denominations });
    setOtSaving(false);
    if (res?.data?.status !== false && res?.status !== "failure") {
      getSweetAlert("success", "On-table chips updated");
    } else {
      getSweetAlert("error", res?.data?.message || res?.message || "Failed to update");
    }
  };

  // ── Deposit Wallet Credit ──
  const [dcSearch, setDcSearch]       = useState("");
  const [dcUser, setDcUser]           = useState(null);
  const [dcSearching, setDcSearching] = useState(false);
  const [dcAmount, setDcAmount]       = useState("");
  const [dcRemarks, setDcRemarks]     = useState("");
  const [dcSubmitting, setDcSubmitting] = useState(false);

  const handleDcSearch = async () => {
    const trimmed = dcSearch.trim();
    if (!trimmed) return;
    setDcSearching(true);
    setDcUser(null);
    const param = /^\d{10}$/.test(trimmed) ? `phone=${trimmed}` : `playerId=${trimmed}`;
    const res = await axiosRequest("GET", `/user_details?${param}`, token);
    setDcSearching(false);
    const user = res?.data?.userDetails;
    if (user) {
      setDcUser(user);
    } else {
      getSweetAlert("error", "User not found");
    }
  };

  const handleDcSubmit = async () => {
    if (!dcUser) return;
    if (!dcAmount || Number(dcAmount) <= 0) { getSweetAlert("error", "Enter a valid amount"); return; }
    setDcSubmitting(true);
    const res = await axiosRequest("POST", "/deposit-credit", token, {
      user_id: dcUser._id,
      amount:  Number(dcAmount),
      remarks: dcRemarks.trim(),
    });
    setDcSubmitting(false);
    if (res?.data?.status !== false && res?.status !== "failure") {
      getSweetAlert("success", res?.message || res?.data?.message || "Deposit credit added");
      // refresh user balances
      const updated = await axiosRequest("GET", `/user_details?playerId=${dcUser.playerId}`, token);
      if (updated?.data?.userDetails) setDcUser(updated.data.userDetails);
      setDcAmount("");
      setDcRemarks("");
    } else {
      getSweetAlert("error", res?.data?.message || res?.message || "Failed to add deposit credit");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Typography variant="h4" fontWeight={700} mb={1}>App Settings</Typography>
      <Typography color="text.secondary" mb={3}>Configure system-wide settings</Typography>

      {/* ── Promo Wallet Conversion ── */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e0e0e0", maxWidth: 480, mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={0.5}>Promo Wallet Conversion</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Percentage of recovered promo amount credited back to promotional wallet on chip cash-out.
          Remaining goes to winning wallet.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
        ) : (
          <>
            <TextField
              fullWidth type="number" label="Promo Back % (0 – 100)"
              value={promoPercent}
              onChange={(e) => setPromoPercent(e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              helperText={
                promoPercent !== ""
                  ? `${100 - Number(promoPercent)}% → Winning  |  ${promoPercent}% → Promo`
                  : " "
              }
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2, px: 4 }}>
              {saving ? "Saving…" : "Save Settings"}
            </Button>
          </>
        )}
      </Paper>

      {/* ── On Table Chips ── */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e0e0e0", maxWidth: 600, mb: 4 }}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <TableChartIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>On Table Chips</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Manually set the chips on table per denomination for the current active shift. This overrides the carried-forward value.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {otChips.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No denominations found. Add denominations in Chips Master first.</Typography>
        ) : (
          <>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Denomination ()</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Quantity on Table</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {otChips.map((chip) => (
                  <TableRow key={chip._id}>
                    <TableCell>
                      <Typography fontWeight={700}>{chip.denomination.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small" type="number"
                        value={otQty[chip.denomination] ?? ""}
                        onChange={(e) => setOtQty({ ...otQty, [chip.denomination]: e.target.value })}
                        inputProps={{ min: 0 }}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button
              variant="contained" onClick={handleOtSave} disabled={otSaving}
              sx={{ borderRadius: 2, px: 4 }}
            >
              {otSaving ? "Saving…" : "Save On Table Chips"}
            </Button>
          </>
        )}
      </Paper>

      {/* ── Deposit Wallet Credit ── */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e0e0e0", maxWidth: 600 }}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <AccountBalanceWalletIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>Deposit Wallet Credit</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Manually credit a player's deposit wallet. Credits both winning balance and deposit total. Transaction is logged with type DEPOSIT_CREDIT.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* User search */}
        <Box display="flex" gap={1.5} mb={2}>
          <TextField
            fullWidth size="small"
            placeholder="Player ID or 10-digit mobile"
            value={dcSearch}
            onChange={(e) => setDcSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleDcSearch()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
            }}
          />
          <Button variant="contained" onClick={handleDcSearch} disabled={dcSearching} sx={{ whiteSpace: "nowrap", borderRadius: 2, px: 3 }}>
            {dcSearching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Box>

        {/* User card */}
        {dcUser && (
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, background: "#f0f4ff", border: "1px solid #c5cae9" }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Avatar sx={{ bgcolor: "#3f51b5", width: 40, height: 40 }}><PersonIcon /></Avatar>
              <Box>
                <Typography fontWeight={700}>{dcUser.name}</Typography>
                <Typography variant="caption" color="text.secondary">ID: {dcUser.playerId} &nbsp;|&nbsp; {maskPhone(dcUser.phone)}</Typography>
              </Box>
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <Box sx={{ p: 1, borderRadius: 1.5, background: "#fff", textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Winning</Typography>
                  <Typography fontWeight={700} color="success.main" fontSize={13}>{Number(dcUser.winning_balance || 0).toLocaleString("en-IN")}</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1, borderRadius: 1.5, background: "#fff", textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Promo</Typography>
                  <Typography fontWeight={700} color="secondary.main" fontSize={13}>{Number(dcUser.promotional_balance || 0).toLocaleString("en-IN")}</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1, borderRadius: 1.5, background: "#fff", textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Total Deposited</Typography>
                  <Typography fontWeight={700} color="primary.main" fontSize={13}>{Number(dcUser.cash_deposite || 0).toLocaleString("en-IN")}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Amount & Remarks */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small" type="number" label="Amount ()"
              value={dcAmount} onChange={(e) => setDcAmount(e.target.value)}
              inputProps={{ min: 1 }} disabled={!dcUser}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth size="small" label="Remarks"
              placeholder="Reason for credit"
              value={dcRemarks} onChange={(e) => setDcRemarks(e.target.value)}
              disabled={!dcUser}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained" color="primary"
          onClick={handleDcSubmit}
          disabled={!dcUser || !dcAmount || dcSubmitting}
          sx={{ borderRadius: 2, fontWeight: 700, px: 4 }}
        >
          {dcSubmitting ? "Adding…" : "Add Deposit Credit"}
        </Button>
      </Paper>
    </Box>
  );
}
